import { router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { NonProfitOrgProfileResponse } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import EditButton from '@/components/buttons/EditButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

const PLItem = ({
  canEdit,
  pl,
  balanceSheet,
  npoId
}: {
  canEdit: boolean;
  pl: NonProfitOrgProfileResponse['plStatement'];
  balanceSheet: NonProfitOrgProfileResponse['balanceSheet'];
  npoId: NonProfitOrgProfileResponse['id'];
}) => {
  const { state } = useAuth();

  const [isLoadingPL, setIsLoadingPL] = useState(false);
  const { showError } = useEndFlowModal();

  const colors = useColor();

  const plParsed = safeJsonParseArray(pl ?? '[]');

  const onPressPL = async () => {
    if (plParsed) {
      setIsLoadingPL(true);
      try {
        let url = firebaseImage(plParsed?.at(0));
        await WebBrowser.openBrowserAsync(url);
      } catch {
        showError({});
      }
      setIsLoadingPL(false);
    }
  };

  return (
    <View>
      <View style={styles.titleContainer}>
        <View style={styles.titleContent}>
          <Text style={[styles.titleText, { color: colors.titleText }]} fontWeight="700">
            {i18n.t('npo_details.insights.pl')}
          </Text>
        </View>
        {canEdit && (
          <EditButton
            onPress={() => {
              if (state.authReady && state.user?.user.role === 'INDIVIDUAL') {
                router.navigate({
                  pathname: '/edit/npoPLWiki',
                  params: {
                    pl: pl,
                    npoId: npoId
                  }
                });
              } else {
                router.navigate({
                  pathname: '/edit/npoPL',
                  params: {
                    pl: pl,
                    balanceSheet: balanceSheet
                  }
                });
              }
            }}
          />
        )}
      </View>
      <View style={styles.content}>
        {!plParsed || plParsed.length === 0 ? (
          <Text> {i18n.t('mix.n_a')}</Text>
        ) : (
          <BrandedButton style={styles.button} onPress={onPressPL} loading={isLoadingPL}>
            {i18n.t('npo_details.insights.pl_button')}
          </BrandedButton>
        )}
      </View>
      <Divider />
    </View>
  );
};

export default PLItem;

const styles = StyleSheet.create({
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  titleContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },

  titleText: {
    fontSize: 22,
    marginRight: 10
  },

  content: {
    marginTop: 10,
    marginBottom: 20
  },

  button: {
    borderRadius: 20,
    width: Platform.OS !== 'web' ? '50%' : '100%',
    height: 34
  },

  row: {
    flexDirection: 'row',
    marginTop: 3
  },

  valueText: {
    minWidth: 80,
    marginRight: 10,
    marginBottom: 8
  }
});
